import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ShieldCheck, Fingerprint, KeyRound, QrCode, Camera,
  Check, X, Lock, ScanFace, Copy, RotateCcw,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { vaultSecurityApi, bioSignature } from "@/api/vaultSecurity";
import { VaultChallenge } from "@/components/dashboard/HubEngines";
import { SecurityOverrideExtras } from "@/components/dashboard/VaultStage7b";
import {
  enrollPasskey, vaultUnlockPasskey, listPasskeys, deletePasskey,
  passkeySupported, passkeyErrorMessage,
} from "@/lib/passkey";
import { Trash2 } from "lucide-react";

/* ==================== shared numeric code entry (len-aware) ============= */
function CodeEntry({ value, onChange, onSubmit, testid, error, len = 6 }) {
  const refs = useRef([]);
  const digits = value.padEnd(len, " ").slice(0, len).split("");
  const set = (i, d) => {
    const c = d.replace(/\D/g, "").slice(-1);
    const arr = value.padEnd(len, " ").slice(0, len).split("");
    arr[i] = c || " ";
    const next = arr.join("").replace(/\s/g, "");
    onChange(next);
    if (c && i < len - 1) refs.current[i + 1]?.focus();
    if (next.length === len) onSubmit?.(next);
  };
  return (
    <div className="flex justify-between gap-2" data-testid={testid}>
      {digits.map((d, i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)} value={d.trim()} inputMode="numeric" maxLength={1}
          onChange={(e) => set(i, e.target.value)}
          onKeyDown={(e) => e.key === "Backspace" && !d.trim() && i > 0 && refs.current[i - 1]?.focus()}
          data-testid={`${testid}-${i}`}
          className={`w-full aspect-square bg-white/10 border-2 rounded-xl text-center font-mono text-2xl font-black text-white shadow-[3px_3px_0px_#000] outline-none focus:ring-2 focus:ring-gold ${error ? "border-red-500" : "border-gold/60"}`} />
      ))}
    </div>
  );
}

/* ============================ TOTP challenge (gate) ===================== */
function TotpChallenge({ onPass, onBack }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (v) => {
    const c = v || code;
    if (c.length !== 6 || busy) return;
    setBusy(true); setError(false);
    try {
      await vaultSecurityApi.totpChallenge(c);
      onPass();
    } catch (e) {
      setError(true); setCode("");
      toast.error(e?.response?.data?.detail || "Invalid authenticator code");
    } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-gate-totp">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="gate-totp-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Second Factor · TOTP</p>
          <h1 className="font-serif text-2xl text-white">Authenticator Challenge</h1>
        </div>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
        <span className="w-14 h-14 rounded-2xl border-2 border-gold bg-white/10 grid place-items-center shadow-[3px_3px_0px_#000]"><KeyRound size={26} className="text-gold" /></span>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 text-center">Enter the 6-digit code from your authenticator app</p>
        <div className="w-full max-w-[280px]"><CodeEntry value={code} onChange={setCode} onSubmit={submit} testid="gate-totp-code" error={error} /></div>
        {busy && <Loader2 size={18} className="text-gold animate-spin" />}
        <button onClick={() => submit()} disabled={busy || code.length !== 6} data-testid="gate-totp-submit" className="w-full max-w-[280px] bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-50 flex items-center justify-center gap-2"><ShieldCheck size={15} /> Verify Code</button>
      </div>
    </div>
  );
}

/* =========================== Biometric challenge (gate) ================= */
function BiometricChallenge({ onPass, onBack }) {
  const { user } = useSession();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch { /* camera optional — signature is still derivable */ }
    })();
    return () => { active = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const scan = async () => {
    setBusy(true); setScanning(true);
    try {
      await new Promise((r) => setTimeout(r, 700)); // scan animation window
      const sig = await bioSignature(user?.user_id);
      await vaultSecurityApi.bioChallenge(sig);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onPass();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Biometric signature mismatch");
    } finally { setBusy(false); setScanning(false); }
  };

  const passkeyUnlock = async () => {
    setBusy(true);
    try {
      await vaultUnlockPasskey();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      toast.success("Unlocked with fingerprint ✓");
      onPass();
    } catch (e) {
      toast.error(passkeyErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-gate-bio">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="gate-bio-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Biometric Array</p>
          <h1 className="font-serif text-2xl text-white">Identity Scan</h1>
        </div>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
        <div className="relative w-44 h-44 rounded-3xl overflow-hidden border-4 border-gold bg-black shadow-[4px_4px_0px_#000]">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="gate-bio-video" />
          <div className={`absolute inset-x-0 h-0.5 bg-gold/80 ${scanning ? "animate-[scan_1.2s_linear_infinite]" : "top-1/2"}`} />
          <div className="absolute inset-0 pointer-events-none grid place-items-center"><ScanFace size={40} className="text-gold/40" /></div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 text-center">Present your face to confirm the vault biometric signature</p>
        <button onClick={scan} disabled={busy} data-testid="gate-bio-scan" className="w-full max-w-[280px] bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-50 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Fingerprint size={15} />} {busy ? "Scanning…" : "Scan & Unlock"}</button>
        {passkeySupported() && (
          <button onClick={passkeyUnlock} disabled={busy} data-testid="gate-bio-passkey" className="w-full max-w-[280px] bg-white/10 text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3 rounded-full border-2 border-gold/50 disabled:opacity-50 flex items-center justify-center gap-2"><Fingerprint size={15} /> Unlock with Device Fingerprint</button>
        )}
      </div>
    </div>
  );
}

/* ---------------------- Forgot Credentials recovery -------------------- */
function RecoveryFlow({ onDone, onBack }) {
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    vaultSecurityApi.recoveryQuestion()
      .then((d) => setQuestion(d.security_question))
      .catch(() => setQuestion(""));
  }, []);

  const submit = async () => {
    if (answer.trim().length < 1 || !/^\d{4,12}$/.test(newPin)) {
      toast.error("Answer + a new 4–12 digit PIN required"); return;
    }
    setBusy(true);
    try {
      await vaultSecurityApi.recover({ security_answer: answer.trim(), new_pin: newPin });
      toast.success("PIN reset — sign in with your new PIN");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-recovery">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="recovery-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Emergency fallback</p>
          <h1 className="font-serif text-2xl text-white">Recover Access</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-6 py-4 space-y-4">
        {question == null ? <div className="flex justify-center py-8"><Loader2 size={20} className="text-gold animate-spin" /></div>
          : !question ? <p className="font-mono text-[11px] text-white/60">No recovery question was set for this vault.</p>
          : (
            <>
              <div className="border-2 border-gold/40 rounded-xl bg-white/5 p-3">
                <p className="font-mono text-[9px] uppercase tracking-widest text-gold/70">Security Question</p>
                <p className="font-serif text-lg text-white mt-1" data-testid="recovery-question-text">{question}</p>
              </div>
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Your Answer</span>
                <input value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="recovery-answer" className="mt-1.5 w-full bg-white/10 border-2 border-gold rounded-xl px-4 py-3 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold shadow-[3px_3px_0px_#000]" />
              </label>
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">New Numeric PIN</span>
                <input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 12))} inputMode="numeric" data-testid="recovery-new-pin" placeholder="4–12 digits" className="mt-1.5 w-full bg-white/10 border-2 border-gold rounded-xl px-4 py-3 font-mono text-lg tracking-[0.4em] text-white outline-none focus:ring-2 focus:ring-gold shadow-[3px_3px_0px_#000]" />
              </label>
              <button onClick={submit} disabled={busy} data-testid="recovery-submit" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Reset PIN</button>
            </>
          )}
      </div>
    </div>
  );
}

/* ===================== STAGED CHALLENGE GATEWAY (Task 3) ================ */
/* Biometric-FIRST: on open, auto-invoke the device passkey (fingerprint/face).
   If cancelled / unavailable / not enrolled → 4-digit PIN → (TOTP) → (bio). */
function BiometricFirstGate({ onPass, onFallback }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const tried = useRef(false);

  const attempt = async () => {
    setBusy(true); setMsg("");
    try {
      await vaultUnlockPasskey();
      toast.success("Unlocked with fingerprint ✓");
      onPass();
    } catch (e) {
      setMsg(passkeyErrorMessage(e) + " — use your PIN instead.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (tried.current) return; tried.current = true;
    (async () => {
      try {
        const d = await listPasskeys();
        if (!(d.credentials || []).length) { onFallback(); return; }  // none enrolled → PIN
        await attempt();  // auto-prompt the platform authenticator
      } catch { onFallback(); }
    })();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-gate-biofirst">
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
        <span className="w-20 h-20 rounded-3xl border-4 border-gold bg-white/10 grid place-items-center shadow-[4px_4px_0px_#000]"><Fingerprint size={40} className="text-gold" /></span>
        <div className="text-center">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Primary factor · Biometrics</p>
          <h1 className="font-serif text-3xl text-white mt-1">Unlock the Vault</h1>
        </div>
        <p className="font-mono text-[11px] text-white/55 text-center max-w-[280px]">{msg || "Confirm with your fingerprint or face to open your secure vault."}</p>
        <button onClick={attempt} disabled={busy} data-testid="biofirst-scan" className="w-full max-w-[280px] bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-50 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Fingerprint size={15} />} {busy ? "Waiting for device…" : "Unlock with Fingerprint / Face"}</button>
        <button onClick={onFallback} data-testid="biofirst-usepin" className="w-full max-w-[280px] bg-white/10 text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3 rounded-full border-2 border-gold/50 flex items-center justify-center gap-2"><KeyRound size={14} /> Use PIN instead</button>
      </div>
    </div>
  );
}

export function VaultGateway({ mode = "pin", onUnlock, onBack, onLockout }) {
  const [profile, setProfile] = useState(null);
  const [hasPasskey, setHasPasskey] = useState(null);
  const [stage, setStage] = useState(null);

  useEffect(() => {
    Promise.all([
      vaultSecurityApi.profile().catch(() => ({ totp_enabled: false, biometric_enabled: false })),
      (passkeySupported() ? listPasskeys().then((d) => (d.credentials || []).length > 0).catch(() => false) : Promise.resolve(false)),
    ]).then(([p, hp]) => {
      setProfile(p); setHasPasskey(hp);
      setStage(hp ? "biofirst" : "pin");  // biometric-first when a passkey exists
    });
  }, []);

  const afterPin = () => {
    if (profile?.totp_enabled) setStage("totp");
    else if (profile?.biometric_enabled) setStage("bio");
    else onUnlock();
  };
  const afterTotp = () => (profile?.biometric_enabled ? setStage("bio") : onUnlock());

  if (!profile || stage === null) {
    return (
      <div className="absolute inset-0 z-30 bg-charcoal grid place-items-center" data-testid="vault-gate-loading">
        <Loader2 size={22} className="text-gold animate-spin" />
      </div>
    );
  }
  if (stage === "biofirst") return <BiometricFirstGate onPass={onUnlock} onFallback={() => setStage("pin")} />;
  if (stage === "recover") return <RecoveryFlow onDone={() => setStage("pin")} onBack={() => setStage("pin")} />;
  if (stage === "pin") return <VaultChallenge mode={mode} onUnlock={afterPin} onBack={onBack} onForgot={() => setStage("recover")} onLockout={onLockout} />;
  if (stage === "totp") return <TotpChallenge onPass={afterTotp} onBack={onBack} />;
  if (stage === "bio") return <BiometricChallenge onPass={onUnlock} onBack={onBack} />;
  return null;
}

/* ===================== VAULT SECURITY SUITE (config) =================== */
/* ==================== Passkeys / device fingerprint (WebAuthn) ========== */
function PasskeyCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => listPasskeys().then((d) => setRows(d.credentials || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const enroll = async () => {
    setBusy(true);
    try {
      const r = await enrollPasskey();
      toast.success(r?.duplicate ? "Passkey already registered" : "Fingerprint / passkey enrolled ✓");
      await load();
    } catch (e) { toast.error(passkeyErrorMessage(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await deletePasskey(id); toast("Passkey removed"); await load(); }
    catch { toast.error("Could not remove passkey"); }
  };
  if (!passkeySupported()) return null;
  return (
    <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-passkey">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><Fingerprint size={16} className="text-gold" /></span>
        <div className="flex-1 leading-tight">
          <p className="font-serif text-lg text-white">Passkeys &amp; Fingerprint</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Device biometrics · WebAuthn</p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full border-2 border-charcoal px-2.5 py-0.5 font-mono text-[9px] font-black uppercase ${rows.length ? "bg-emerald-500 text-white" : "bg-white/10 text-white/50"}`} data-testid="sec-passkey-status">
          {rows.length ? <Check size={11} /> : <X size={11} strokeWidth={3} />} {rows.length || "Off"}
        </span>
      </div>
      <p className="font-mono text-[10px] text-white/50 leading-relaxed mt-2">Use your device fingerprint / Face ID (or a security key) for passwordless sign-in and to unlock the vault. Keys are device-bound and never leave your device.</p>
      <div className="mt-2.5 space-y-1.5" data-testid="sec-passkey-list">
        {rows.map((c) => (
          <div key={c.credential_id} data-testid={`sec-passkey-${c.credential_id.slice(0, 8)}`} className="flex items-center gap-2 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2">
            <Fingerprint size={13} className="text-gold shrink-0" />
            <div className="flex-1 min-w-0 leading-tight">
              <p className="font-mono text-[10px] font-black text-white truncate">{c.label || "Passkey"}</p>
              <p className="font-mono text-[8px] uppercase tracking-widest text-white/40">{(c.created_at || "").slice(0, 10)}{c.last_used_at ? " · used" : ""}</p>
            </div>
            <button onClick={() => remove(c.credential_id)} data-testid={`sec-passkey-del-${c.credential_id.slice(0, 8)}`} className="w-7 h-7 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 active:scale-90"><Trash2 size={12} className="text-white/50" /></button>
          </div>
        ))}
      </div>
      <button onClick={enroll} disabled={busy} data-testid="sec-passkey-enroll" className="mt-2.5 w-full bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none flex items-center justify-center gap-2">{busy ? <Loader2 size={13} className="animate-spin" /> : <Fingerprint size={13} />} Enroll fingerprint / passkey</button>
    </div>
  );
}

export function VaultSecuritySuite({ onBack }) {
  const { user, setUser } = useSession();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [totp, setTotp] = useState(null); // { secret, qr_data_url }
  const [confirmCode, setConfirmCode] = useState("");

  const load = () => vaultSecurityApi.profile().then(setProfile).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncUser = (p) => setUser((u) => ({ ...u, vault_2fa_enabled: p.totp_enabled, vault_biometric_enabled: p.biometric_enabled }));

  /* ---- Biometric Array ---- */
  const enrollBio = async () => {
    setBusy(true);
    try {
      const sig = await bioSignature(user?.user_id);
      const { profile: p } = await vaultSecurityApi.bioEnroll(sig);
      setProfile(p); syncUser(p);
      toast.success("Biometric array armed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not arm biometrics"); } finally { setBusy(false); }
  };
  const disableBio = async () => {
    setBusy(true);
    try { const { profile: p } = await vaultSecurityApi.bioDisable(); setProfile(p); syncUser(p); toast("Biometric array disarmed"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  /* ---- 2FA / TOTP ---- */
  const startTotp = async () => {
    setBusy(true);
    try { const d = await vaultSecurityApi.totpInit(); setTotp(d); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start 2FA"); } finally { setBusy(false); }
  };
  const confirmTotp = async (code) => {
    const c = code || confirmCode;
    if (c.length !== 6) return;
    setBusy(true);
    try {
      const { profile: p } = await vaultSecurityApi.totpConfirm(c);
      setProfile(p); syncUser(p); setTotp(null); setConfirmCode("");
      toast.success("Authenticator 2FA enabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } finally { setBusy(false); }
  };
  const disableTotp = async () => {
    setBusy(true);
    try { const { profile: p } = await vaultSecurityApi.totpDisable(); setProfile(p); syncUser(p); toast("2FA disabled"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const copySecret = () => { navigator.clipboard?.writeText(totp?.secret || ""); toast("Secret copied"); };

  const StatusPill = ({ on }) => (
    <span className={`inline-flex items-center gap-1 rounded-full border-2 border-charcoal px-2.5 py-0.5 font-mono text-[9px] font-black uppercase ${on ? "bg-emerald-500 text-white" : "bg-white/10 text-white/50"}`} data-testid="sec-status-pill">
      {on ? <Check size={11} /> : <X size={11} strokeWidth={3} />} {on ? "Armed" : "Off"}
    </span>
  );

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-security-suite">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
        <button onClick={onBack} data-testid="sec-suite-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Advanced Core Vault</p>
          <h1 className="font-serif text-2xl text-white">Security Suite</h1>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
        {/* PIN status (already set on entry) */}
        <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-pin">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><Lock size={16} className="text-gold" /></span>
            <div className="flex-1 leading-tight">
              <p className="font-serif text-lg text-white">Master {profile?.vault_mode === "pass" ? "Passphrase" : "PIN"}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Primary factor</p>
            </div>
            <StatusPill on={!!profile?.pin_set} />
          </div>
        </div>

        {/* BIOMETRIC ARRAY */}
        <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-bio">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><Fingerprint size={16} className="text-gold" /></span>
            <div className="flex-1 leading-tight">
              <p className="font-serif text-lg text-white">Biometric Array</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Face signature factor</p>
            </div>
            <StatusPill on={!!profile?.biometric_enabled} />
          </div>
          <p className="font-mono text-[10px] text-white/50 leading-relaxed mt-2">Binds a device identity signature to the vault. A live scan re-derives the same signature to unlock.</p>
          {profile?.biometric_enabled ? (
            <button onClick={disableBio} disabled={busy} data-testid="sec-bio-disable" className="mt-3 w-full bg-white/10 text-white/70 border-2 border-cream/30 font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full">Disarm Biometrics</button>
          ) : (
            <button onClick={enrollBio} disabled={busy} data-testid="sec-bio-enroll" className="mt-3 w-full bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none flex items-center justify-center gap-2">{busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />} Arm Biometric Array</button>
          )}
        </div>

        {/* PASSKEYS / DEVICE FINGERPRINT (WebAuthn) */}
        <PasskeyCard />

        {/* 2FA / TOTP */}
        <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-totp">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><KeyRound size={16} className="text-gold" /></span>
            <div className="flex-1 leading-tight">
              <p className="font-serif text-lg text-white">Authenticator 2FA</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">TOTP · Google / Authy</p>
            </div>
            <StatusPill on={!!profile?.totp_enabled} />
          </div>

          {totp ? (
            <div className="mt-3 space-y-3" data-testid="sec-totp-setup">
              <div className="bg-white rounded-2xl border-2 border-gold p-3 grid place-items-center">
                <img src={totp.qr_data_url} alt="Scan with authenticator" className="w-40 h-40" data-testid="sec-totp-qr" />
              </div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 text-center">Scan in Google Authenticator / Authy</p>
              <button onClick={copySecret} data-testid="sec-totp-copy" className="w-full flex items-center justify-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-gold border-2 border-gold/40 rounded-full py-1.5"><Copy size={11} /> Copy setup key</button>
              <div><p className="font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1.5 text-center">Enter the current 6-digit code</p>
                <CodeEntry value={confirmCode} onChange={setConfirmCode} onSubmit={confirmTotp} testid="sec-totp-confirm-code" /></div>
              <button onClick={() => confirmTotp()} disabled={busy || confirmCode.length !== 6} data-testid="sec-totp-confirm" className="w-full bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#000] disabled:opacity-50 flex items-center justify-center gap-2">{busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Confirm & Enable</button>
            </div>
          ) : profile?.totp_enabled ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={startTotp} disabled={busy} data-testid="sec-totp-cycle" className="bg-white/10 text-gold border-2 border-gold/50 font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full flex items-center justify-center gap-1.5"><RotateCcw size={13} /> Cycle Secret</button>
              <button onClick={disableTotp} disabled={busy} data-testid="sec-totp-disable" className="bg-white/10 text-white/70 border-2 border-cream/30 font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full">Disable 2FA</button>
            </div>
          ) : (
            <button onClick={startTotp} disabled={busy} data-testid="sec-totp-start" className="mt-3 w-full bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none flex items-center justify-center gap-2">{busy ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />} Set Up Authenticator</button>
          )}
        </div>

        {/* C4 — Security Override Desk: Challenge Q&A + Master PIN mutator */}
        <SecurityOverrideExtras />
      </div>
    </div>
  );
}
